// Кнопка дії кабінету — єдиний стандарт із HAG_cab_style_compliance.md §6:
// height 36, radius 10, фон --surface-2, контур --line-strong, тінь --shadow.
//
// Догма §2 прямо забороняє брендовий червоний як «просто важливе»: --err
// лишається за реальним збоєм, а звичайні Save / Add / Edit / Continue не є
// помилкою й не фарбуються. Тому «головна» дія тут виділяється НАСИЧЕНІСТЮ
// заливки, а не кольором.
//
// `tone="danger"` існує тільки для справді руйнівних дій (видалення) — там
// червоний семантично доречний.
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

type Tone = 'primary' | 'ghost' | 'danger';

const TONES: Record<Tone, { bg: string; border: string; color: string; hover: string }> = {
  // Головна дія: помітніша заливка нейтральним, без кольору.
  primary: {
    bg: 'color-mix(in srgb, var(--text) 10%, transparent)',
    border: 'var(--line-strong)',
    color: 'var(--text)',
    hover: 'color-mix(in srgb, var(--text) 16%, transparent)',
  },
  // Другорядна дія: прозора.
  ghost: {
    bg: 'transparent',
    border: 'var(--line-strong)',
    color: 'var(--text-muted)',
    hover: 'color-mix(in srgb, var(--text) 8%, transparent)',
  },
  // Руйнівна дія — єдине місце, де червоний доречний за §2.
  danger: {
    bg: 'color-mix(in srgb, var(--err) 12%, transparent)',
    border: 'color-mix(in srgb, var(--err) 40%, transparent)',
    color: 'var(--err)',
    hover: 'color-mix(in srgb, var(--err) 20%, transparent)',
  },
};

interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  tone?: Tone;
  Icon?: LucideIcon;
  /** Компактний варіант для щільних рядів (28px). */
  compact?: boolean;
  children?: ReactNode;
}

export default function ActionButton({
  tone = 'primary', Icon, compact, children, style, disabled, ...rest
}: Props) {
  const c = TONES[tone];
  return (
    <button
      {...rest}
      disabled={disabled}
      className="inline-flex items-center justify-center gap-2 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      style={{
        height: compact ? 28 : 36,
        borderRadius: compact ? 8 : 10,
        padding: compact ? '0 10px' : '0 14px',
        background: c.bg,
        border: `1px solid ${c.border}`,
        color: c.color,
        boxShadow: 'var(--shadow)',
        fontSize: compact ? 12 : 13,
        fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer',
        ...style,
      }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = c.hover; }}
      onMouseLeave={(e) => { if (!disabled) e.currentTarget.style.background = c.bg; }}
    >
      {Icon && <Icon size={compact ? 13 : 15} strokeWidth={2} style={{ flexShrink: 0 }} />}
      {children}
    </button>
  );
}
